<template>
  <Card class="mb-0 gap-2 py-3" data-testid="dashboard-unread-messages-card">
    <CardHeader class="border-b border-gray-200 px-4 pb-2">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <CardTitle>Unread messages</CardTitle>
          <Badge variant="gray">{{ totalUnreadThreads }}</Badge>
        </div>

        <Button as-child size="sm" variant="card-header-action">
          <router-link to="/inbox" data-testid="dashboard-view-all-messages">View inbox</router-link>
        </Button>
      </div>
    </CardHeader>

    <CardContent class="px-0">
      <div v-if="messages.length === 0" class="flex flex-1 items-center justify-center px-6 py-6">
        <div class="flex max-w-sm flex-col items-center text-center">
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <MessageSquare class="h-5 w-5" />
          </div>
          <p class="mt-4 text-sm font-medium text-slate-900">No unread messages</p>
          <p class="mt-1 text-sm text-slate-500">All candidate messages have been read.</p>
          <Button as-child size="sm" variant="outline" class="mt-4">
            <router-link to="/inbox">View inbox</router-link>
          </Button>
        </div>
      </div>

      <router-link
        v-for="message in messages"
        :key="message.contact_id"
        to="/inbox"
        class="block border-b border-gray-200 px-4 py-2.5 last:border-b-0"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="min-w-0 space-y-1">
            <div class="flex flex-wrap items-center gap-2">
              <p class="truncate text-sm font-medium text-slate-900">
                {{ message.name }}
              </p>
              <Badge
                v-if="message.unread_count > 1"
                variant="gray"
                class="px-1.5 py-0 text-[11px] font-medium normal-case"
              >
                {{ message.unread_count }} unread
              </Badge>
            </div>
            <p class="line-clamp-1 text-xs text-slate-500">
              {{ message.body }}
            </p>
          </div>

          <div class="flex shrink-0 items-center gap-3 pl-3 text-xs text-slate-500">
            <span>{{ fromNowWithSuffix(message.created_at) }}</span>
            <ChevronRight class="h-3.5 w-3.5" />
          </div>
        </div>
      </router-link>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import type { DashboardUnreadMessage } from '@/api/dashboard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronRight, MessageSquare } from 'lucide-vue-next';
import DateUtility from '@/utilities/dates';

defineProps<{
  messages: DashboardUnreadMessage[];
  totalUnreadThreads: number;
}>();

const { fromNowWithSuffix } = DateUtility;
</script>
